import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { Popover, PopoverContent, PopoverTrigger } from '~/components/ui/popover';
import { cn } from '~/lib/utils';
import type { MenuItem } from '~/lib/menu';
import { useActiveMenuPath } from '~/hooks/use-active-menu-path';

/**
 * Collapsed-mode parent affordance: an icon-only trigger that reveals a hover panel
 * (portaled, so it escapes the sidebar's overflow) listing the parent label + its
 * children. Replaces the accordion when the sidebar is collapsed (5.C.2).
 */
export function SidebarFlyout({ item, active }: { item: MenuItem; active: boolean }) {
	const { t } = useTranslation();
	const activePath = useActiveMenuPath();
	const [open, setOpen] = useState(false);
	const Icon = item.icon;

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<button
					type="button"
					onMouseEnter={() => setOpen(true)}
					onMouseLeave={() => setOpen(false)}
					aria-label={t(item.labelKey)}
					className={cn(
						'flex h-7 w-full items-center justify-center rounded-md transition-colors',
						active ? 'border-l-[3px] border-l-brand bg-brand-subtle' : 'hover:bg-muted'
					)}>
					<Icon size={16} className={cn('shrink-0', active ? 'text-brand-subtle-fg' : 'text-foreground-subtle')} />
				</button>
			</PopoverTrigger>
			<PopoverContent
				side="right"
				align="start"
				sideOffset={8}
				onMouseEnter={() => setOpen(true)}
				onMouseLeave={() => setOpen(false)}
				className="w-[200px] p-1.5">
				<div className="px-2 py-1 text-[12.5px] font-medium text-foreground">{t(item.labelKey)}</div>
				<div className="mt-1 flex flex-col">
					{item.children?.map(child => {
						const childActive = child.path === activePath;
						return (
							<Link
								key={child.id}
								to={child.path}
								onClick={() => setOpen(false)}
								className={cn(
									'rounded-md px-2 py-1 text-[11.5px] transition-colors',
									childActive ? 'bg-brand-subtle font-medium text-brand-subtle-fg' : 'text-foreground-muted hover:bg-muted'
								)}>
								{t(child.labelKey)}
							</Link>
						);
					})}
				</div>
			</PopoverContent>
		</Popover>
	);
}
